import { useMemo, useRef, useState, useLayoutEffect } from "react";
import { ZoomIn, ZoomOut, Maximize, Grid3x3, Ruler, Palette, Columns2, Compass } from "lucide-react";
import { buildPrimitives, cellRect, visibleCells } from "@/lib/geometry";
import { jointPrims } from "@/lib/joints";
import { SourcePane } from "@/components/SourcePane";
import { Primitives } from "@/components/Primitives";
import { hasPlan, planSketch } from "@/lib/corners";

const PlanOverlay = ({ item, palette }) => {
  const { prims, bounds: b } = planSketch(item);
  return (
    <div className="absolute z-10 left-3 bottom-3 w-60 rounded-md border border-slate-700 bg-[#0d1220]/90 backdrop-blur-md p-2 fade-in pointer-events-none" data-testid="plan-sketch">
      <p className="font-mono text-[10px] uppercase tracking-wider text-pink-300 mb-1">Plan · top view · corner angles</p>
      <svg viewBox={`${b.x} ${b.y} ${b.w} ${b.h}`} className="w-full max-h-40"><Primitives prims={prims} u={b.w / 260} palette={palette} /></svg>
    </div>
  );
};

const ToolBtn = ({ active, onClick, children, testid, title }) => (
  <button data-testid={testid} title={title} onClick={onClick}
    className={`h-8 w-8 grid place-items-center rounded-md border transition-colors duration-200 ${active ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-300" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
    {children}
  </button>
);

export const ElevationCanvas = ({ item, selected, onSelectCell, leading, projectId }) => {
  const [zoom, setZoom] = useState(1);
  const [grid, setGrid] = useState(true);
  const [dims, setDims] = useState(true);
  const [mode, setMode] = useState("dark");
  const [compare, setCompare] = useState(false);
  const [showPlan, setShowPlan] = useState(true);
  const plan = hasPlan(item);
  const wrap = useRef(null);
  const [px, setPx] = useState(800);
  useLayoutEffect(() => {
    const ro = new ResizeObserver(([e]) => setPx(e.contentRect.width || 800));
    if (wrap.current) ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  const { prims, bounds } = useMemo(() => {
    const b = buildPrimitives(item, { dims });
    return { ...b, prims: [...b.prims, ...jointPrims(item)] };
  }, [item, dims]);
  const u = Math.max(bounds.w, bounds.h) / (Math.min(px, 1000) * zoom);
  const bg = mode === "blueprint" ? "bg-[#0B2A6B]" : "bg-[#0B0F19]";

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0" data-testid="elevation-canvas">
      <div className="h-12 px-3 flex items-center gap-2 border-b border-slate-800 bg-[#0d1220]">
        {leading}
        <ToolBtn testid="zoom-in-btn" title="Zoom in" onClick={() => setZoom((z) => Math.min(4, z * 1.25))}><ZoomIn size={16} /></ToolBtn>
        <ToolBtn testid="zoom-out-btn" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.4, z / 1.25))}><ZoomOut size={16} /></ToolBtn>
        <ToolBtn testid="zoom-fit-btn" title="Fit" onClick={() => setZoom(1)}><Maximize size={16} /></ToolBtn>
        <span className="font-mono text-xs text-slate-500 w-12" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
        <div className="w-px h-6 bg-slate-800 mx-1" />
        <ToolBtn testid="toggle-grid-btn" title="Grid" active={grid} onClick={() => setGrid(!grid)}><Grid3x3 size={16} /></ToolBtn>
        <ToolBtn testid="toggle-dims-btn" title="Dimensions" active={dims} onClick={() => setDims(!dims)}><Ruler size={16} /></ToolBtn>
        <ToolBtn testid="compare-source-btn" title={item.source ? "Compare with source joinery drawing" : "No source crop for this item"} active={compare && !!item.source} onClick={() => item.source && setCompare(!compare)}><Columns2 size={16} /></ToolBtn>
        {plan && <ToolBtn testid="toggle-plan-btn" title="Plan sketch (corner angles)" active={showPlan} onClick={() => setShowPlan(!showPlan)}><Compass size={16} /></ToolBtn>}
        <ToolBtn testid="toggle-mode-btn" title="Blueprint mode" active={mode === "blueprint"} onClick={() => setMode(mode === "dark" ? "blueprint" : "dark")}><Palette size={16} /></ToolBtn>
        <span className="ml-auto font-mono text-[11px] uppercase tracking-wider text-slate-500 hidden md:block">{plan ? "Developed elevation · " : "Elevation · "}viewed from outside · mm</span>
      </div>
      <div className="flex-1 flex min-h-0 relative">
      {compare && item.source && <SourcePane projectId={projectId} item={item} />}
      {plan && showPlan && <PlanOverlay item={item} palette={mode} />}
      <div ref={wrap} className={`flex-1 overflow-auto relative ${bg} ${grid ? "cad-grid" : ""}`}>
        <div className="min-h-full grid place-items-center p-8" style={{ width: `${zoom * 100}%` }}>
          <svg data-testid="elevation-svg" viewBox={`${bounds.x} ${bounds.y} ${bounds.w} ${bounds.h}`}
            style={{ width: "100%", maxHeight: zoom === 1 ? "calc(100vh - 220px)" : "none" }} className="max-w-[1000px] fade-in">
            <Primitives prims={prims} u={u} palette={mode} />
            {visibleCells(item).map(({ r, c }) => {
              const cr = cellRect(item, r, c);
              const sel = selected && selected.r === r && selected.c === c;
              return (
                <rect key={`${r}-${c}`} data-testid={`canvas-cell-${r}-${c}`} x={cr.x} y={cr.y} width={cr.w} height={cr.h}
                  fill={sel ? "rgba(6,182,212,0.14)" : "transparent"} stroke={sel ? "#22D3EE" : "transparent"}
                  strokeWidth={2.5 * u} strokeDasharray={`${8 * u} ${4 * u}`} className="cursor-pointer hover:fill-cyan-400/10"
                  onClick={() => onSelectCell({ r, c })} />
              );
            })}
          </svg>
        </div>
      </div>
      </div>
    </div>
  );
};
